import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Building2, Phone, MapPin, FileText, ArrowRight, User, KeyRound, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { userService, agencyService, authService } from '../lib/databaseService';

export default function AgenciesPage({ agencies, listings, user, onLogin, onLogout, users = [], onUpdateUsers }) {
  const [showCorporateLogin, setShowCorporateLogin] = useState(false);
  const [activeTab, setActiveTab] = useState('agent'); // Ana sekme: 'agent' veya 'owner'
  const [agentSubTab, setAgentSubTab] = useState('agent-login'); // Emlakçı alt sekmeleri: 'agent-login' veya 'agent-register'
  
  // Emlakçı - Giriş formu (YENİ: Ad Soyad, Telefon, Agentlik Kodu)
  const [agentLoginName, setAgentLoginName] = useState('');
  const [agentLoginSurname, setAgentLoginSurname] = useState('');
  const [agentLoginPhone, setAgentLoginPhone] = useState('');
  const [agentLoginAccessCode, setAgentLoginAccessCode] = useState('');
  
  // Emlakçı - Kayıt formu
  const [agentRegName, setAgentRegName] = useState('');
  const [agentRegSurname, setAgentRegSurname] = useState('');
  const [agentRegPhone, setAgentRegPhone] = useState('');
  const [agentRegAccessCode, setAgentRegAccessCode] = useState('');
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  
  // Acente Sahibi - Giriş formu
  const [ownerPhone, setOwnerPhone] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [isOwnerLoggingIn, setIsOwnerLoggingIn] = useState(false);

  const getAgencyListingsCount = (agencyId) => {
    return listings.filter(l => l.agencyId === agencyId).length;
  };

  // Emlakçı Giriş (YENİ: Ad Soyad, Telefon, Agentlik Kodu)
  const handleAgentLogin = async (e) => {
    e.preventDefault();
    
    if (!agentLoginName || !agentLoginSurname || !agentLoginPhone || !agentLoginAccessCode) {
      toast.error('Bütün sahələri doldurun');
      return;
    }

    try {
      // Agentlik kodunu kontrol et
      const agency = await agencyService.findByAccessCode(agentLoginAccessCode);
      
      if (!agency) {
        toast.error('Yanlış agentlik kodu və ya agentlik aktiv deyil');
        return;
      }

      // Telefon numarası ile kullanıcıyı bul
      const foundUser = await userService.findByPhone(agentLoginPhone);
      
      if (!foundUser) {
        toast.error('Bu telefon nömrəsi ilə qeydiyyatlı əmlakçı tapılmadı');
        return;
      }

      // Kullanıcının ad soyad ve agentlik kontrolü
      if (foundUser.name.toLowerCase() !== agentLoginName.toLowerCase() || 
          foundUser.surname?.toLowerCase() !== agentLoginSurname.toLowerCase()) {
        toast.error('Ad, soyad və ya telefon nömrəsi uyğun deyil');
        return;
      }

      // Agentlik ID kontrolü
      if (foundUser.agency_id !== agency.id) {
        toast.error('Bu agentlik kodu ilə qeydiyyatlı deyilsiniz');
        return;
      }

      // Kullanıcıyı giriş yap
      const userData = {
        id: foundUser.id,
        name: foundUser.name,
        surname: foundUser.surname || '',
        email: foundUser.email,
        phone: foundUser.phone,
        agencyId: foundUser.agency_id,
        isAgent: true,
      };

      onLogin(userData);
      toast.success(`Xoş gəldiniz, ${foundUser.name}!`);
      
      // Formu temizle ve modalı kapat
      setAgentLoginName('');
      setAgentLoginSurname('');
      setAgentLoginPhone('');
      setAgentLoginAccessCode('');
      setShowCorporateLogin(false);
    } catch (error) {
      console.error('Agent login error:', error);
      toast.error('Giriş zamanı xəta baş verdi');
    }
  };

  // Emlakçı Kayıt
  const handleAgentRegister = async (e) => {
    e.preventDefault();
    
    if (!agentRegName || !agentRegSurname || !agentRegPhone || !agentRegAccessCode) {
      toast.error('Bütün sahələri doldurun');
      return;
    }

    setIsVerifyingCode(true);

    try {
      // Ajans kodunu kontrol et
      const agency = await agencyService.findByAccessCode(agentRegAccessCode);

      if (!agency) {
        toast.error('Yanlış ajans kodu və ya agentlik aktiv deyil');
        setIsVerifyingCode(false);
        return;
      }

      // Telefon numarası zaten kayıtlı mı kontrol et
      const existingUser = await userService.findByPhone(agentRegPhone);
      if (existingUser) {
        toast.error('Bu telefon nömrəsi artıq qeydiyyatdadır');
        setIsVerifyingCode(false);
        return;
      }

      // Yeni emlakçı kullanıcısı oluştur
      const newAgent = {
        name: agentRegName,
        surname: agentRegSurname,
        phone: agentRegPhone,
        email: `agent-${Date.now()}@mydom.az`, // Geçici email
        password: '', // Şifre gerektirmiyoruz şimdilik
        agencyId: agency.id,
        isAgent: true,
      };

      const createdUser = await userService.register(newAgent);

      // Kullanıcıyı otomatik giriş yap
      const userData = {
        id: createdUser.id,
        name: createdUser.name,
        surname: createdUser.surname || '',
        email: createdUser.email,
        phone: createdUser.phone,
        agencyId: createdUser.agency_id,
        isAgent: true,
      };

      onLogin(userData);
      toast.success(`Xoş gəldiniz, ${agentRegName}! ${agency.name} agentliyinə bağlandınız.`);
      
      // Formu temizle ve modalı kapat
      setAgentRegName('');
      setAgentRegSurname('');
      setAgentRegPhone('');
      setAgentRegAccessCode('');
      setShowCorporateLogin(false);
    } catch (error) {
      console.error('Agent registration error:', error);
      toast.error('Qeydiyyat zamanı xəta baş verdi');
    } finally {
      setIsVerifyingCode(false);
    }
  };

  // Acente Sahibi Giriş
  const handleOwnerLogin = async (e) => {
    e.preventDefault();
    
    if (!ownerPhone || !ownerPassword) {
      toast.error('Bütün sahələri doldurun');
      return;
    }

    if (ownerPassword.length !== 6) {
      toast.error('Şifrə 6 rəqəmli olmalıdır');
      return;
    }

    setIsOwnerLoggingIn(true);

    try {
      const agency = await agencyService.ownerLogin(ownerPhone, ownerPassword);

      if (!agency) {
        toast.error('Telefon nömrəsi və ya şifrə yanlışdır');
        setIsOwnerLoggingIn(false);
        return;
      }

      // Acente sahibi olarak giriş yap
      const ownerData = {
        id: agency.id,
        name: agency.name,
        email: agency.email || '',
        phone: agency.phone,
        isAgencyOwner: true,
        agencyId: agency.id,
      };

      onLogin(ownerData);
      toast.success(`Xoş gəldiniz, ${agency.name} agentliyinin sahibi!`);
      
      // Formu temizle ve modalı kapat
      setOwnerPhone('');
      setOwnerPassword('');
      setShowCorporateLogin(false);
    } catch (error) {
      console.error('Owner login error:', error);
      toast.error('Giriş zamanı xəta baş verdi');
    } finally {
      setIsOwnerLoggingIn(false);
    }
  };


  return (
    <>
      <div className="app-container" data-testid="agencies-page">
        <Header user={user} onLogin={onLogin} onLogout={onLogout} users={users} onUpdateUsers={onUpdateUsers} agencies={agencies} />
        
        {/* Hero */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 logo-text mb-3">Agentliklər</h1>
              <p className="text-lg text-gray-600">Etibarlı agentliklərdən elanlar</p>
            </div>
            {/* Kurumsal Giriş Butonu */}
            <Button
              onClick={() => setShowCorporateLogin(true)}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white"
            >
              Kurumsal Giriş
            </Button>
          </div>

          {/* Kurumsal Giriş Modal */}
          {showCorporateLogin && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-6 md:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 logo-text">Kurumsal Giriş</h2>
                  <button
                    onClick={() => {
                      setShowCorporateLogin(false);
                      setActiveTab('agent');
                      setAgentSubTab('agent-login');
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={24} />
                  </button>
                </div>
        
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="agent" className="text-base">Əmlakçı</TabsTrigger>
                    <TabsTrigger value="owner" className="text-base">Agentlik Sahibi</TabsTrigger>
                  </TabsList>
        
                  {/* ƏMLAKÇI BÖLMƏSİ */}
                  <TabsContent value="agent" className="space-y-6">
                    <Tabs value={agentSubTab} onValueChange={setAgentSubTab} className="w-full">
                      <TabsList className="grid w-full grid-cols-2 mb-4">
                        <TabsTrigger value="agent-login">Giriş</TabsTrigger>
                        <TabsTrigger value="agent-register">Qeydiyyat</TabsTrigger>
                      </TabsList>
        
                      {/* Emlakçı Giriş (YENİ: Ad Soyad, Telefon, Agentlik Kodu) */}
                      <TabsContent value="agent-login" className="space-y-4 pt-4">
                        <form onSubmit={handleAgentLogin} className="space-y-4">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label>Ad *</Label>
                              <div className="relative">
                                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <Input
                                  value={agentLoginName}
                                  onChange={(e) => setAgentLoginName(e.target.value)}
                                  placeholder="Adınız"
                                  className="pl-10"
                                  required
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label>Soyad *</Label>
                              <div className="relative">
                                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <Input
                                  value={agentLoginSurname}
                                  onChange={(e) => setAgentLoginSurname(e.target.value)}
                                  placeholder="Soyadınız"
                                  className="pl-10"
                                  required
                                />
                              </div>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Telefon Nömrəsi *</Label>
                            <div className="relative">
                              <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                              <Input
                                type="tel"
                                value={agentLoginPhone}
                                onChange={(e) => setAgentLoginPhone(e.target.value)}
                                placeholder="+994 XX XXX XX XX"
                                className="pl-10"
                                required
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Agentlik Kodu *</Label>
                            <div className="relative">
                              <KeyRound size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                              <Input
                                type="text"
                                value={agentLoginAccessCode}
                                onChange={(e) => setAgentLoginAccessCode(e.target.value.toUpperCase())}
                                placeholder="RMX-2024"
                                className="pl-10 tracking-widest"
                                required
                                maxLength={20}
                              />
                            </div>
                          </div>
                          <Button type="submit" className="w-full bg-[#B91C1C] hover:bg-[#991B1B]">
                            Daxil ol
                          </Button>
                        </form>
                      </TabsContent>
        
                      {/* Emlakçı Kayıt */}
                      <TabsContent value="agent-register" className="space-y-4 pt-4">
                        <form onSubmit={handleAgentRegister} className="space-y-4">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label>Ad *</Label>
                              <div className="relative">
                                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <Input
                                  value={agentRegName}
                                  onChange={(e) => setAgentRegName(e.target.value)}
                                  placeholder="Adınız"
                                  className="pl-10"
                                  required
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label>Soyad *</Label>
                              <div className="relative">
                                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <Input
                                  value={agentRegSurname}
                                  onChange={(e) => setAgentRegSurname(e.target.value)}
                                  placeholder="Soyadınız"
                                  className="pl-10"
                                  required
                                />
                              </div>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Telefon Nömrəsi *</Label>
                            <div className="relative">
                              <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                              <Input
                                type="tel"
                                value={agentRegPhone}
                                onChange={(e) => setAgentRegPhone(e.target.value)}
                                placeholder="+994 XX XXX XX XX"
                                className="pl-10"
                                required
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Agentlik Kodu *</Label>
                            <div className="relative">
                              <KeyRound size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                              <Input
                                type="text"
                                value={agentRegAccessCode}
                                onChange={(e) => setAgentRegAccessCode(e.target.value.toUpperCase())}
                                placeholder="RMX-2024"
                                className="pl-10 tracking-widest"
                                required
                                maxLength={20}
                              />
                            </div>
                            <p className="text-xs text-gray-500">
                              Agentliyinizdən aldığınız agentlik kodunu daxil edin
                            </p>
                          </div>
                          <Button
                            type="submit"
                            className="w-full bg-[#B91C1C] hover:bg-[#991B1B]"
                            disabled={isVerifyingCode || !agentRegName || !agentRegSurname || !agentRegPhone || !agentRegAccessCode}
                          >
                            {isVerifyingCode ? 'Yoxlanılır...' : 'Qeydiyyatdan keç'}
                          </Button>
                        </form>
                      </TabsContent>
                    </Tabs>
                  </TabsContent>
        
                  {/* AGENTLİK SAHİBİ BÖLMƏSİ */}
                  <TabsContent value="owner" className="space-y-4 pt-4">
                    <form onSubmit={handleOwnerLogin} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Telefon Nömrəsi *</Label>
                        <div className="relative">
                          <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <Input
                            type="tel"
                            value={ownerPhone}
                            onChange={(e) => setOwnerPhone(e.target.value)}
                            placeholder="+994 XX XXX XX XX"
                            className="pl-10"
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>6 Rəqəmli Xüsusi Şifrə *</Label>
                        <div className="relative">
                          <KeyRound size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <Input
                            type="text"
                            value={ownerPassword}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                              setOwnerPassword(value);
                            }}
                            placeholder="000000"
                            className="pl-10 text-center tracking-widest text-lg font-mono"
                            maxLength={6}
                            required
                          />
                        </div>
                        <p className="text-xs text-gray-500">
                          Agentliyinizə verilən 6 rəqəmli xüsusi şifrəni daxil edin
                        </p>
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-[#B91C1C] hover:bg-[#991B1B]"
                        disabled={isOwnerLoggingIn || !ownerPhone || ownerPassword.length !== 6}
                      >
                        {isOwnerLoggingIn ? 'Giriş edilir...' : 'Daxil ol'}
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          )}
        </div>
      </section>
        
      {/* Agencies List - Yenilənmiş Dizayn */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="space-y-4" data-testid="agencies-grid">
            {agencies.map((agency) => (
              <Link
                key={agency.id}
                to={`/agencies/${agency.id}`}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden card-hover transition-all hover:shadow-lg flex items-center gap-4 p-4 md:p-6"
                data-testid={`agency-card-${agency.id}`}
              >
                {/* Yuvarlaq Loqo */}
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-200 flex items-center justify-center">
                    {agency.logo ? (
                      <img 
                        src={agency.logo} 
                        alt={agency.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Building2 size={32} className="text-gray-400" />
                    )}
                  </div>
                </div>
        
                {/* Agentlik Məlumatları */}
                <div className="flex-1 min-w-0">
                  {/* Başlıq və Ad */}
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 truncate">{agency.name}</h3>
                  </div>
                  
                  {/* Təsvir */}
                  {agency.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{agency.description}</p>
                  )}
                  
                  {/* Əlaqə Məlumatları */}
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                    {agency.phone && (
                      <div className="flex items-center gap-2">
                        <Phone size={14} className="text-[#B91C1C] flex-shrink-0" />
                        <span className="truncate">{agency.phone}</span>
                      </div>
                    )}
                    {agency.address && (
                      <div className="flex items-center gap-2">
                        <MapPin size={14} className="text-[#B91C1C] flex-shrink-0" />
                        <span className="truncate">{agency.address}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-[#B91C1C] flex-shrink-0" />
                      <span>{getAgencyListingsCount(agency.id)} elan</span>
                    </div>
                  </div>
        
                  {/* Elanları Gör Düyməsi */}
                  <div className="flex items-center text-[#B91C1C] font-medium text-sm">
                    <span>Elanları gör</span>
                    <ArrowRight size={16} className="ml-1" />
                  </div>
                </div>
              </Link>
            ))}
            
            {agencies.length === 0 && (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                <Building2 size={48} className="text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Hələ heç bir agentlik yoxdur</p>
              </div>
            )}
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
